import { useCallback, useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "@/hooks";
import * as actions from "@/redux/LandingPageReducer";

const usePackages = ({
  showSelectPackage,
  pageType,
  price,
  selectedPackage,
  defaultSelectedPackDuration,
  defaultSelectedPack,
  discount,
  goalId,
  timezone,
  isPartnershipPage,
}) => {
  const dispatch = useAppDispatch();

  const { isPricingLoading, pricingData } = useAppSelector(
    (state) => state.landingPage
  );

  const { pricing } = pricingData || {};

  const [packageOptions, setPackageOptions] = useState([]);
  const [selectedPack, setSelectedPack] = useState(null);
  const [priceObj, setPriceObj] = useState(price);

  useEffect(() => {
    setPriceObj(price);
  }, [price]);

  useEffect(() => {
    if (!showSelectPackage) return;
    if (goalId) {
      const productTypeMap = {
        YOGA_PARTNERSHIP: "ZOOM_YOGA",
        NUTRITION_PARTNERSHIP: "NUTRITION",
        HEALTH_PARTNERSHIP: "HEALTH_PROGRAM",
        DERMA_PARTNERSHIP: "DERMA",
      };

      dispatch(
        actions.getPackagePricing({
          nutrition_goal_id: goalId,
          product_type: productTypeMap[pageType],
          timeZone: timezone,
          ...(isPartnershipPage && { pageType }),
        })
      );
    }
  }, [
    dispatch,
    showSelectPackage,
    goalId,
    pageType,
    timezone,
    isPartnershipPage,
  ]);

  useEffect(() => {
    if (!showSelectPackage) return;

    const productTypeMap = {
      YOGA_PARTNERSHIP: "Yoga",
      NUTRITION_PARTNERSHIP: "Nutrition",
      HEALTH_PARTNERSHIP: "Health",
      DERMA_PARTNERSHIP: "Dermatology",
    };

    const productType = productTypeMap[pageType];

    const _packageOptions =
      pricing?.map((pac) => ({
        value: pac?.package_slug || pac?.slug,
        label: `${pac?.title} ${productType} Plan: ${pac?.display_price}`,
        ...pac,
      })) || [];

    setPackageOptions(_packageOptions);
  }, [pricing, showSelectPackage, pageType]);

  const setSelectedPackData = useCallback(
    (pack) => {
      const _selectedPack = {
        title: pack?.title,
        currency: pack?.currency,
        currency_symbol: pack?.currency_symbol,
        price: Number(pack?.price),
        price_text: pack?.display_price,
        button_cta: "Buy now",
        primary_btn_cta: `Buy for ${pack?.currency} ${pack?.price}`,
        primary_btn_cta_constant: `Buy for ${pack?.currency} `,
        package_slug: pack?.package_slug,
      };

      setSelectedPack(_selectedPack);

      const discountAmount = discount
        ? Math.ceil((_selectedPack?.price * discount) / 100)
        : 0;
      const discountedPrice = _selectedPack?.price - discountAmount;

      setPriceObj((prevState) => ({
        ...prevState,
        price: discountedPrice,
        original_price: _selectedPack?.price,
        currency: _selectedPack?.currency,
        currency_symbol: _selectedPack?.currency_symbol,
      }));
    },
    [discount]
  );

  useEffect(() => {
    if (!showSelectPackage) return;
    let pack;

    const defaultSelectedPackByDuration =
      defaultSelectedPackDuration && goalId && packageOptions?.length;

    if (defaultSelectedPackByDuration) {
      pack =
        packageOptions?.find(
          (p) => p?.duration === defaultSelectedPackDuration
        ) || {};
    } else if (defaultSelectedPack) {
      pack = defaultSelectedPack;
    } else if (selectedPackage) {
      pack = pricing?.find(
        (p) => (p?.package_slug || p?.slug) === selectedPackage
      );
    }

    if (pack) setSelectedPackData(pack);
  }, [
    showSelectPackage,
    packageOptions,
    pricing,
    setSelectedPackData,
    selectedPackage,
    defaultSelectedPackDuration,
    defaultSelectedPack,
    goalId,
  ]);

  return {
    isPricingLoading,
    packageOptions,
    selectedPack,
    priceObj,
  };
};

export default usePackages;
